import { beginNavigationFeedback } from '@ui/NavigationFeedback'
import { requestClinicalNoteLeave } from '@zustore/store'
import { useCallback } from 'react'
import { NavigateOptions as RouterNavigateOptions, To, useLocation, useNavigate } from 'react-router-dom'

type NavigateCallback = () => void

type GuardedNavigateOptions = Omit<RouterNavigateOptions, 'replace' | 'state'> & {
  replace?: boolean
  state?: any
}

export const useGuardedNavigate = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const guardedNavigate = useCallback(
    (to: To, options?: GuardedNavigateOptions, callback?: NavigateCallback) => {
      requestClinicalNoteLeave(() => {
        beginNavigationFeedback()
        navigate(to, options)
        callback?.()
      })
    },
    [navigate]
  )

  const guardedSearchParams = useCallback(
    (
      updater: (params: URLSearchParams) => URLSearchParams | void,
      options?: GuardedNavigateOptions,
      callback?: NavigateCallback
    ) => {
      const params = new URLSearchParams(location.search)
      const updatedParams = updater(params) || params
      requestClinicalNoteLeave(() => {
        beginNavigationFeedback()
        navigate(
          {
            pathname: location.pathname,
            search: updatedParams.toString() ? `?${updatedParams.toString()}` : ''
          },
          { replace: options?.replace, state: options?.state }
        )
        callback?.()
      })
    },
    [location.pathname, location.search, navigate]
  )

  return { guardedNavigate, guardedSearchParams }
}
